import { useEffect, useState, useCallback } from "react";
import { apiFetch } from "@/lib/supabase";
import { useVelvet } from "@/context/VelvetContext";
import { fmtDate } from "@/lib/calc";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Sparkles, StickyNote, Save, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const STAGES = ['Nuevo', 'Contactado', 'Calificado', 'Cotizado', 'Ganado', 'Perdido'];
const SOURCES = ['Instagram', 'WhatsApp', 'Referido', 'Web', 'Otro'];

export default function LeadDetail({ leadId, onClose, onChanged, canAssignOwner, staff = [] }) {
  const { customers } = useVelvet();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [noteBody, setNoteBody] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);

  const [form, setForm] = useState({
    name: '', email: '', phone: '', company: '',
    source: 'Otro', stage: 'Nuevo', owner_id: '',
    estimated_value: '', occasion: '', lost_reason: '',
  });

  const load = useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      const data = await apiFetch(`/crm/leads/${leadId}`);
      setLead(data);
      setForm({
        name: data.name || '', email: data.email || '', phone: data.phone || '',
        company: data.company || '', source: data.source || 'Otro',
        stage: data.stage || 'Nuevo', owner_id: data.owner_id || '',
        estimated_value: data.estimated_value ?? '',
        occasion: data.occasion || '', lost_reason: data.lost_reason || '',
      });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (v) => setForm({ ...form, [k]: v?.target ? v.target.value : v });

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.estimated_value === '' || payload.estimated_value === null) {
        payload.estimated_value = null;
      } else payload.estimated_value = Number(payload.estimated_value);
      if (!payload.owner_id) delete payload.owner_id;
      await apiFetch(`/crm/leads/${leadId}`, { method: 'PATCH', body: JSON.stringify(payload) });
      toast.success('Lead actualizado');
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const addNote = async () => {
    if (!noteBody.trim()) return;
    setAddingNote(true);
    try {
      await apiFetch('/crm/notes', {
        method: 'POST',
        body: JSON.stringify({ lead_id: leadId, body: noteBody.trim() }),
      });
      setNoteBody("");
      toast.success('Nota agregada');
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setAddingNote(false);
    }
  };

  const doConvert = async (body) => {
    try {
      await apiFetch(`/crm/leads/${leadId}/convert`, {
        method: 'POST', body: JSON.stringify(body),
      });
      toast.success('Lead convertido a cliente');
      setConvertOpen(false);
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (loading || !lead) {
    return <div className="p-8 text-center text-muted-foreground" data-testid="lead-detail-loading">Cargando…</div>;
  }

  return (
    <div className="p-6 sm:p-8" data-testid="lead-detail">
      <div className="mb-6">
        <div className="velvet-eyebrow mb-2">Lead</div>
        <div className="flex flex-wrap items-baseline gap-3">
          <h2 className="font-serif text-3xl tracking-tight">{lead.name}</h2>
          {lead.customer_id && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 border border-[hsl(140_30%_50%)] bg-[hsl(140_40%_92%)]"
                  data-testid="lead-converted-badge">
              <Sparkles className="h-3 w-3" /> Convertido — {lead.customer_name}
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-2">Actualizado {fmtDate(lead.updated_at)} · {lead.notes_count || (lead.notes?.length ?? 0)} nota(s)</p>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <div className="space-y-4">
          <div><Label>Nombre</Label><Input value={form.name} onChange={set('name')} className="rounded-sm" data-testid="edit-lead-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Email</Label><Input value={form.email} onChange={set('email')} className="rounded-sm" /></div>
            <div><Label>Teléfono</Label><Input value={form.phone} onChange={set('phone')} className="rounded-sm" /></div>
          </div>
          <div><Label>Empresa</Label><Input value={form.company} onChange={set('company')} className="rounded-sm" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Fuente</Label>
              <Select value={form.source} onValueChange={set('source')}>
                <SelectTrigger className="rounded-sm" data-testid="edit-lead-source"><SelectValue /></SelectTrigger>
                <SelectContent>{SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Etapa</Label>
              <Select value={form.stage} onValueChange={set('stage')}>
                <SelectTrigger className="rounded-sm" data-testid="edit-lead-stage"><SelectValue /></SelectTrigger>
                <SelectContent>{STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Ocasión</Label><Input value={form.occasion} onChange={set('occasion')} className="rounded-sm" /></div>
            <div><Label>Valor estimado ($)</Label><Input type="number" value={form.estimated_value} onChange={set('estimated_value')} className="rounded-sm" /></div>
          </div>
          {canAssignOwner && (
            <div>
              <Label>Responsable</Label>
              <Select value={form.owner_id} onValueChange={set('owner_id')}>
                <SelectTrigger className="rounded-sm"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                <SelectContent>
                  {staff.map(s => <SelectItem key={s.id} value={s.id}>{s.full_name || s.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {form.stage === 'Perdido' && (
            <div><Label>Motivo de pérdida</Label><Textarea value={form.lost_reason} onChange={set('lost_reason')} className="rounded-sm" data-testid="edit-lead-lost-reason" /></div>
          )}
          <div className="flex gap-2 pt-2">
            <Button onClick={save} disabled={saving}
                    className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
                    data-testid="save-lead-changes-btn">
              <Save className="h-4 w-4 mr-1" /> Guardar cambios
            </Button>
            {!lead.customer_id && (
              <Button variant="outline" className="rounded-sm"
                      onClick={() => setConvertOpen(true)}
                      data-testid="convert-lead-btn">
                <ArrowRight className="h-4 w-4 mr-1" /> Convertir a cliente
              </Button>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="velvet-eyebrow flex items-center gap-2"><StickyNote className="h-3.5 w-3.5" /> Notas</div>
          <div className="space-y-2">
            <Textarea value={noteBody} onChange={(e) => setNoteBody(e.target.value)}
                      placeholder="Añadir una nota…"
                      className="rounded-sm min-h-[80px]" data-testid="new-note-input" />
            <Button size="sm" disabled={addingNote || !noteBody.trim()} onClick={addNote}
                    className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
                    data-testid="add-note-btn">
              Guardar nota
            </Button>
          </div>
          <div className="space-y-3 pt-2 max-h-[420px] overflow-y-auto" data-testid="notes-list">
            {(lead.notes || []).length === 0 && (
              <p className="text-sm text-muted-foreground">Sin notas todavía.</p>
            )}
            {(lead.notes || []).map((n) => (
              <div key={n.id} className="border border-border p-3 bg-card" data-testid={`note-${n.id}`}>
                <div className="text-sm whitespace-pre-wrap">{n.body}</div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mt-2">
                  {n.author_name || '—'} · {fmtDate(n.created_at)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-8 flex justify-end">
        <Button variant="outline" className="rounded-sm" onClick={onClose} data-testid="close-lead-detail-btn">Cerrar</Button>
      </div>

      <ConvertDialog open={convertOpen} onOpenChange={setConvertOpen}
                     lead={lead} customers={customers} onSubmit={doConvert} />
    </div>
  );
}

function ConvertDialog({ open, onOpenChange, lead, customers, onSubmit }) {
  const [mode, setMode] = useState('new');  // 'new' | 'link'
  const [customerId, setCustomerId] = useState('');
  const [address, setAddress] = useState('');
  const [notes, setNotes] = useState('');

  const submit = () => {
    if (mode === 'link') {
      if (!customerId) return;
      onSubmit({ customer_id: customerId });
    } else {
      onSubmit({ create_customer: true, address, notes });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-sm" data-testid="convert-lead-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Convertir lead a cliente</DialogTitle>
          <DialogDescription>Vincula {lead.name} a un cliente existente o crea uno nuevo.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="flex gap-2">
            <button type="button" onClick={() => setMode('new')}
                    className={`flex-1 border px-3 py-2 text-sm rounded-sm ${mode === 'new' ? 'bg-foreground text-background border-foreground' : 'border-border bg-card'}`}
                    data-testid="convert-mode-new">
              Crear cliente nuevo
            </button>
            <button type="button" onClick={() => setMode('link')}
                    className={`flex-1 border px-3 py-2 text-sm rounded-sm ${mode === 'link' ? 'bg-foreground text-background border-foreground' : 'border-border bg-card'}`}
                    data-testid="convert-mode-link">
              Vincular existente
            </button>
          </div>
          {mode === 'new' ? (
            <>
              <div className="text-xs text-muted-foreground">
                Se creará con: {lead.name} · {lead.email || '—'} · {lead.phone || '—'}
              </div>
              <div><Label>Dirección (opcional)</Label><Input value={address} onChange={(e) => setAddress(e.target.value)} className="rounded-sm" /></div>
              <div><Label>Notas (opcional)</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-sm" /></div>
            </>
          ) : (
            <div>
              <Label>Cliente existente</Label>
              <Select value={customerId} onValueChange={setCustomerId}>
                <SelectTrigger className="rounded-sm" data-testid="convert-existing-select">
                  <SelectValue placeholder="Elegir cliente…" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit}
                  disabled={mode === 'link' && !customerId}
                  className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
                  data-testid="confirm-convert-btn">
            Convertir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
